import React, { useState } from 'react';

import { useSubmitFeedback } from '@api/aiReviews/api.aiReviews.queries';

import { FeedbackDialogView } from './FeedbackDialogView';

import type { FeedbackDialogProperties } from './FeedbackDialog.types';

/**
 * FeedbackDialog - Container component for feedback modal
 * Manages rating/comment state and submits feedback via API
 */
export const FeedbackDialog: React.FC<FeedbackDialogProperties> = ({
  open,
  reviewId,
  onClose,
  dataTest = 'feedback_dialog',
}) => {
  const [rating, setRating] = useState<number>();
  const [comment, setComment] = useState('');

  const { mutate: submitMutation, isPending } = useSubmitFeedback(reviewId);

  /*
   * Оценка приходит аргументом, а не читается из состояния (AF-351).
   *
   * Здесь стоял гард `if (rating === undefined) return;` — он повторял то, что
   * уже сделано в представлении: кнопка отправки рисуется настоящим
   * `<button disabled>` при `disabled={rating === undefined || isSubmitting}`,
   * то есть без оценки клика не бывает и ложная ветвь гарда недостижима.
   * Проверить её тестом можно было бы только обойдя `disabled`.
   *
   * Сужение типа при этом никуда не делось — оно переехало в `FeedbackDialogView`,
   * в тело рендера, где обе ветви настоящие: диалог открывается без оценки и
   * получает её кликом по шкале.
   */
  const handleSubmit = (submittedRating: number) => {
    submitMutation(
      {
        satisfaction_rating: submittedRating,
        feedback_comment: comment,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <FeedbackDialogView
      open={open}
      rating={rating}
      comment={comment}
      isSubmitting={isPending}
      onRatingChange={setRating}
      onCommentChange={setComment}
      onSubmit={handleSubmit}
      onSkip={handleSkip}
      dataTest={dataTest}
    />
  );
};
